import { createContext, useContext, useState, useCallback, useEffect } from "react";
import translations from "./translations";

const LanguageContext = createContext();

/**
 * Detect browser language.
 * Returns "de" if the browser's primary language starts with "de" (covers de-DE, de-AT, de-CH).
 * Returns "en" for everything else.
 */
function detectLanguage() {
  const browserLang = navigator.language || navigator.userLanguage || "en";
  return browserLang.startsWith("de") ? "de" : "en";
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(detectLanguage);

  // Update <html lang="..."> and page title when language changes
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = translations[lang]["meta.title"];
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", translations[lang]["meta.description"]);
    }
  }, [lang]);

  /**
   * Translate a key, with optional interpolation.
   * Usage: t("upload.domainsFound", { count: 42 }) \u2192 "42 domains found"
   */
  const t = useCallback(
    (key, params) => {
      let text = translations[lang]?.[key] || translations.en[key] || key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          text = text.replace(`{${k}}`, v);
        });
      }
      return text;
    },
    [lang]
  );

  const toggleLanguage = useCallback(() => {
    setLang((prev) => (prev === "en" ? "de" : "en"));
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
